import { Alert, Card, Group, Loader, Stack, Switch, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBrand, updateBrand } from "@/api/tenant";
import { useMenuLanguages } from "@/hooks/useMenuLanguages";
import { ListError } from "@/components/DataList";
import { notify } from "@/lib/notify";

export function MenuSettings() {
  const queryClient = useQueryClient();
  const {
    data: brand,
    isPending,
    isError,
    refetch,
  } = useQuery({ queryKey: ["brand"], queryFn: getBrand });
  const { selectedCodes, base } = useMenuLanguages();

  // Anahtar YOKSA açık: ayar eklenmeden önceki menülerin davranışı buydu (bkz. api schema
  // Tenant.menuSettings) ve panelin varsayılanı ile menünün varsayılanı ayrışmamalı.
  const languageGate = brand?.menuSettings?.languageGate !== false;

  const mutation = useMutation({
    // Kolonun tamamı yazılıyor, kısmi birleştirme yok: yeni bir anahtar eklendiğinde onu da
    // buraya koymak gerekecek ve unutulursa kaybolduğu ANINDA görülüyor — sunucuda sessizce
    // birleştirmek, hangi ayarın nereden geldiğini okunmaz hale getirirdi.
    mutationFn: (next) =>
      updateBrand({ menuSettings: { ...brand?.menuSettings, ...next } }),
    onSuccess: (_data, next) => {
      notify.success(
        next.languageGate
          ? "Dil seçim ekranı açıldı"
          : "Dil seçim ekranı kapatıldı",
      );
      queryClient.invalidateQueries({ queryKey: ["brand"] });
    },
    onError: () => notify.error("Ayar kaydedilemedi"),
  });

  if (isError) {
    return <ListError title="Menü ayarları alınamadı" onRetry={refetch} />;
  }

  // Tek dilde kapı zaten hiç çıkmıyor. Anahtarı gizlemek yerine bunu SÖYLÜYORUZ: kapalı bir
  // anahtar "kapattım" diye okunur, oysa ayar açık ve etkisi yok.
  const singleLanguage = selectedCodes.length <= 1;

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="lg">
        <div>
          <Text fw={600}>Açılış Ekranı</Text>
          <Text c="dimmed" size="sm">
            Müşteri QR menüyü açtığında dil sorulsun mu
          </Text>
        </div>

        {isPending ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <Switch
            checked={languageGate}
            disabled={mutation.isPending}
            onChange={(event) =>
              mutation.mutate({ languageGate: event.currentTarget.checked })
            }
            label="Açılışta dil seçimi sorulsun"
            description={
              languageGate
                ? "Menü açılır açılmaz dil listesi çıkıyor; müşteri seçmeden menüyü görmüyor."
                : `Menü doğrudan ${base?.label ?? "taban dilde"} açılıyor. Dil değiştirme menü içinde duruyor.`
            }
          />
        )}

        {!isPending && singleLanguage && (
          <Alert
            variant="light"
            color="gray"
            icon={<IconInfoCircle size={18} />}
          >
            Şu an tek dil açık, o yüzden bu ekran zaten çıkmıyor. Yukarıdan
            ikinci bir dil açtığında bu ayar devreye girer.
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
